import React from 'react';
import { motion } from 'framer-motion';
import { Box, Typography, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Surrogate } from '../types';
//import { Bot, User } from 'lucide-react';
import AvatarChip from '../../../components/common/AvatarChip.tsx';

interface SurrogateCardProps {
  surrogate: Surrogate;
}

const CardContainer = styled(motion(Box))(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(3),
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(5px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'linear-gradient(0deg, transparent, transparent 30%, rgba(0,255,255,0.3))',
    transform: 'rotate(-45deg)',
    transition: 'all 0.5s ease',
    opacity: 0,
  },
  '&:hover': {
    background: 'rgba(26, 26, 46, 0.8)',
    transform: 'scale(1.05)',
    boxShadow: '0 0 20px rgba(0,255,255,0.5)',
    '&::before': {
      opacity: 1,
      transform: 'rotate(-45deg) translateY(100%)',
    },
  }
}));

const SkillChip = styled(Chip)(({ theme }) => ({
  background: 'rgba(0, 255, 255, 0.1)',
  color: '#0ff',
  border: '1px solid rgba(0, 255, 255, 0.2)',
  margin: theme.spacing(0.5),
  '&:hover': {
    background: 'rgba(0, 255, 255, 0.2)',
  }
}));

const SurrogateCard: React.FC<SurrogateCardProps> = ({ surrogate }) => {
  // Determine the color based on surrogate type
  const getAvatarColor = () => {
    return surrogate.type === 'ai' ? '#00CCFF' : '#FF6F00';
  };

  return (
    <CardContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <AvatarChip
          name={surrogate.name}
          role={surrogate.type === 'ai' ? 'AI System' : 'Human Surrogate'}
          avatarColor={getAvatarColor()}
          avatarSize="medium"
          status={surrogate.type === 'ai' ? 'active' : 'idle'}
        />
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'rgba(255, 255, 255, 0.8)',
            mb: 1
          }}
        >
          Core Skills:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {surrogate.coreSkills.map((skill) => (
            <SkillChip 
              key={skill} 
              label={skill}
              size="small"
            />
          ))}
        </Box>
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.6)',
              mb: 0.5
            }}
          >
            Automation
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#0ff',
              textShadow: '0 0 10px rgba(0,255,255,0.5)'
            }}
          >
            {surrogate.automationCapability}/10
          </Typography>
        </Box>
        <Box>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.6)',
              mb: 0.5
            }}
          >
            Efficiency
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#0ff',
              textShadow: '0 0 10px rgba(0,255,255,0.5)'
            }}
          >
            {surrogate.efficiency}/10
          </Typography>
        </Box>
      </Box>
    </CardContainer>
  );
};

export default SurrogateCard; 